export enum DeliveryCode {
    Standard = 0,
    Overnight = 1,
    WillCall = 2,
    TwoDay = 3,
    StandardOvernight = 4,
    FirstClass = 5,
    USPSPriority = 6,
    Delivery = 7,
    FedExGround = 8,
    FedExHomeDelivery = 9,
    FedEx2Day = 10,
    FedEx2DayAM = 11,
    FedExExpressSaver = 12,
    FedExPriorityOvernight = 13,
    FedExFirstOvernight = 14,
    FedExSmartPost = 15,
    UPSGround = 16,
    UPSNextDayAir = 17,
    UPSNextDayAirSaver = 18,
    UPS2ndDayAir = 19,
    USPSGroundAdvantage = 20,
}

/**
 * RequiredCarrier semantics:
 *   "FedEx" | "USPS" | "UPS" — value is bound to that carrier; picker hides it when carrier is not allowed.
 *   null  — generic speed tier; picker always shows it (the rate-shop picks any allowed carrier).
 *   ""    — no carrier involved (pickup / local delivery); picker always shows it.
 *
 * ServiceCode is the ShippingServiceCatalog code passed to FedEx Direct or ShipEngine when this value is forced.
 * Priority is the cascade rank — lower wins in the Prescription > Drug > Program > Patient > CII default chain.
 * Mirrors `CPHub.Utility.ShippingCarriers.DeliveryCodeCarrierMap.Entry.Priority` (keep in lockstep).
 */
export interface DeliveryCodeMapEntry {
    requiredCarrier: "FedEx" | "USPS" | "UPS" | "" | null;
    serviceCode: string | null;
    /** Carrier brand standards govern this string — FedEx requires spaced service marks. Display only; never matched on. */
    displayName: string;
    priority: number;
}

export const DeliveryCodeCarrierMap: Record<DeliveryCode, DeliveryCodeMapEntry> = {
    [DeliveryCode.WillCall]:               { requiredCarrier: "",      serviceCode: null,                       displayName: "Will Call",                priority:  0 },
    [DeliveryCode.FedExFirstOvernight]:    { requiredCarrier: "FedEx", serviceCode: "FIRST_OVERNIGHT",          displayName: "FedEx First Overnight",    priority:  1 },
    [DeliveryCode.FedExPriorityOvernight]: { requiredCarrier: "FedEx", serviceCode: "PRIORITY_OVERNIGHT",       displayName: "FedEx Priority Overnight", priority:  2 },
    [DeliveryCode.UPSNextDayAir]:          { requiredCarrier: "UPS",   serviceCode: "ups_next_day_air",         displayName: "UPS Next Day Air",         priority:  3 },
    [DeliveryCode.UPSNextDayAirSaver]:     { requiredCarrier: "UPS",   serviceCode: "ups_next_day_air_saver",   displayName: "UPS Next Day Air Saver",   priority:  4 },
    [DeliveryCode.Overnight]:              { requiredCarrier: null,    serviceCode: null,                       displayName: "Overnight",                priority:  5 },
    [DeliveryCode.StandardOvernight]:      { requiredCarrier: "FedEx", serviceCode: "STANDARD_OVERNIGHT",       displayName: "FedEx Standard Overnight", priority:  6 },
    [DeliveryCode.FedEx2DayAM]:            { requiredCarrier: "FedEx", serviceCode: "FEDEX_2_DAY_AM",           displayName: "FedEx 2Day A.M.",          priority:  7 },
    [DeliveryCode.FedEx2Day]:              { requiredCarrier: "FedEx", serviceCode: "FEDEX_2_DAY",              displayName: "FedEx 2Day",               priority:  8 },
    [DeliveryCode.UPS2ndDayAir]:           { requiredCarrier: "UPS",   serviceCode: "ups_2nd_day_air",          displayName: "UPS 2nd Day Air",          priority:  9 },
    [DeliveryCode.TwoDay]:                 { requiredCarrier: null,    serviceCode: null,                       displayName: "Two Day",                  priority: 10 },
    [DeliveryCode.FedExExpressSaver]:      { requiredCarrier: "FedEx", serviceCode: "FEDEX_EXPRESS_SAVER",      displayName: "FedEx Express Saver",      priority: 11 },
    [DeliveryCode.FedExGround]:            { requiredCarrier: "FedEx", serviceCode: "FEDEX_GROUND",             displayName: "FedEx Ground",             priority: 12 },
    [DeliveryCode.FedExHomeDelivery]:      { requiredCarrier: "FedEx", serviceCode: "GROUND_HOME_DELIVERY",     displayName: "FedEx Home Delivery",      priority: 13 },
    [DeliveryCode.UPSGround]:              { requiredCarrier: "UPS",   serviceCode: "ups_ground",               displayName: "UPS Ground",               priority: 14 },
    [DeliveryCode.USPSPriority]:           { requiredCarrier: "USPS",  serviceCode: "usps_priority_mail",       displayName: "USPS Priority",            priority: 15 },
    [DeliveryCode.FirstClass]:             { requiredCarrier: "USPS",  serviceCode: "usps_first_class_mail",    displayName: "USPS First Class",         priority: 16 },
    [DeliveryCode.USPSGroundAdvantage]:    { requiredCarrier: "USPS",  serviceCode: "usps_ground_advantage",    displayName: "USPS Ground Advantage",    priority: 17 },
    [DeliveryCode.FedExSmartPost]:         { requiredCarrier: "FedEx", serviceCode: "SMART_POST",               displayName: "FedEx Ground Economy",     priority: 18 },
    [DeliveryCode.Standard]:               { requiredCarrier: null,    serviceCode: null,                       displayName: "Standard",                 priority: 19 },
    [DeliveryCode.Delivery]:               { requiredCarrier: "",      serviceCode: null,                       displayName: "Delivery",                 priority: 20 },
};

/**
 * Cascade ranking derived from `DeliveryCodeCarrierMap` by priority. Lower priority wins.
 * Used to reduce candidate DeliveryCodes (Prescription > Drug > Program > Patient > CII default) to one winner.
 * Adding a new DeliveryCode is a single edit to the map above — this list auto-updates.
 */
export const DeliveryCodeImportanceRank: DeliveryCode[] =
    (Object.entries(DeliveryCodeCarrierMap) as [string, DeliveryCodeMapEntry][])
        .sort(([, a], [, b]) => a.priority - b.priority)
        .map(([k]) => Number(k) as DeliveryCode);

export const DeliveryCodeDisplayNames: Array<{ enum: DeliveryCode; name: string }> =
    (Object.values(DeliveryCode)
        .filter((v): v is DeliveryCode => typeof v === "number"))
        .map(code => ({ enum: code, name: DeliveryCodeCarrierMap[code].displayName }));
